<template>
  <div class="relative z-[100]">
    <Overlay :visible="isOpen" @click="close" />
    <Drawer
      ref="activeOffersSidebarRef"
      class="active-offers-sidebar__aside"
      :is-open="isOpen"
      @click:close="close"
    >
      <template #header>
        <div class="px-4 py-4 border-b border-gray-200">
          <h2 class="text-lg font-semibold text-gray-900">
            {{ t('general.yourRequests') }}
          </h2>
        </div>
      </template>
      <div v-if="isOpen" class="flex flex-col flex-1 overflow-y-auto px-4">
        <div v-if="activeOffers.length === 0" class="px-4 py-8 text-center">
          <p class="text-gray-500">{{ t('general.noActiveOffers') }}</p>
        </div>
        <div v-else>
          <h3 class="mb-4 text-base font-medium text-black">
            {{ t('general.hereAreSomeClassesYouMightLike') }}
          </h3>
          <div class="space-y-2">
            <div v-for="offer in activeOffers" :key="offer.id" class="py-2">
              <div class="flex gap-3">
                <!-- Image -->
                <div class="flex-shrink-0">
                  <img
                    v-if="offer.image"
                    :src="offer.image"
                    :alt="offer.title || ''"
                    class="w-[140px] min-h-[112px] h-full object-cover rounded"
                    loading="lazy"
                  />
                </div>

                <!-- Content -->
                <div class="flex-1 flex flex-col min-w-0">
                  <!-- Title -->
                  <h4
                    v-if="offer.title"
                    class="text-xs font-medium text-black mb-1 line-clamp-2"
                  >
                    {{ offer.title }}
                  </h4>

                  <!-- Date and People (inline) -->
                  <div
                    v-if="offer.firstDate || offer.requestedTickets"
                    class="flex items-center gap-2 mb-0.5"
                    style="font-size: 12px"
                  >
                    <span
                      v-if="offer.firstDate"
                      class="flex items-center gap-1"
                      style="
                        color: rgba(75, 85, 99, 0.77);
                        font-weight: 400;
                        line-height: 133%;
                      "
                    >
                      <IconDuration class="w-3 h-3 flex-shrink-0" />
                      <span>
                        {{ formatDate(offer.firstDate) }}
                        <span v-if="offer.totalDates && offer.totalDates > 1">
                          + {{ offer.totalDates - 1 }}
                          {{ t('general.moreDates') }}
                        </span>
                      </span>
                    </span>
                    <span
                      v-if="offer.requestedTickets"
                      class="flex items-center gap-1"
                      style="
                        color: rgba(75, 85, 99, 0.77);
                        font-weight: 400;
                        line-height: 133%;
                      "
                    >
                      <IconPeople class="w-3 h-3 flex-shrink-0" />
                      {{ offer.requestedTickets }}
                      {{
                        offer.requestedTickets === 1
                          ? t('general.perPerson')
                          : t('general.people')
                      }}
                    </span>
                  </div>

                  <!-- Location (separate line) -->
                  <div
                    v-if="offer.location"
                    class="mb-0.5 flex items-center gap-1"
                    style="
                      color: rgba(75, 85, 99, 0.77);
                      font-weight: 400;
                      font-size: 12px;
                      line-height: 133%;
                    "
                  >
                    <IconLocation class="w-3 h-3 flex-shrink-0" />
                    {{ offer.location }}
                  </div>

                  <!-- Price -->
                  <div
                    v-if="offer.price"
                    class="mt-auto"
                    style="
                      font-weight: 700;
                      font-size: 12px;
                      line-height: 125%;
                      text-align: left;
                      color: #111928;
                    "
                  >
                    {{ formatPrice(offer.price, offer.priceCurrency) }}
                  </div>

                  <!-- Button -->
                  <div class="mt-2">
                    <Button
                      color="primary"
                      variant="outlined"
                      :link="getOfferUrl(offer)"
                      class="w-full flex items-center justify-center h-[28px] text-sm font-semibold"
                      @click="close"
                    >
                      {{ t('general.previewEvent') }}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { formatPriceAmount } from '~/utils/currencyHelpers'
import { useOfferTracking } from '~/composables/useOfferTracking'
import { useActiveOffersSidebarUiState } from '~/composables/useUiState'
import IconDuration from '~/assets/offer/offer-icon-duration.svg?component'
import IconLocation from '~/assets/offer/offer-icon-location.svg?component'
import IconPeople from '~/assets/offer/offer-icon-people.svg?component'

const { t, locale } = useI18n()
const localePath = useLocalePath()
const { isOpen: isOpenRef, close } = useActiveOffersSidebarUiState()
const { activeOffers, removeOfferPage } = useOfferTracking()

// Ensure isOpen is always a ref for watch
const isOpen = isRef(isOpenRef) ? isOpenRef : ref(isOpenRef)

// Check and remove expired offers based on lastDate
const removeExpiredOffers = () => {
  const now = Date.now()
  const offers = [...activeOffers.value] // Copy array to avoid mutation during iteration

  offers.forEach((offer) => {
    if (offer.lastDate) {
      const lastDateTimestamp = new Date(offer.lastDate).getTime()
      // Check if the last available date has passed
      if (lastDateTimestamp < now) {
        removeOfferPage(offer.id)
      }
    }
  })
}

const getOfferUrl = (offer: { id: string; signature?: string }) => {
  const baseUrl = localePath(`/offer/${offer.id}`)
  if (offer.signature) {
    return `${baseUrl}?signature=${offer.signature}`
  }
  return baseUrl
}

const formatDate = (dateString: string) => {
  if (!dateString) return ''
  return dayjs(dateString).format('dd, DD.MM.YY')
}

const formatPrice = (price: number, currency?: string) => {
  return formatPriceAmount(price, locale.value, currency || 'EUR')
}

// Watch for sidebar open to check expired offers
watch(isOpen, (newValue) => {
  if (newValue) {
    // When sidebar opens, check and remove expired offers
    removeExpiredOffers()
  }
})
</script>

<style lang="postcss" scoped>
.active-offers-sidebar__aside {
  @apply w-full sm:w-[600px] px-0 pt-0 pb-0 flex flex-col h-[100dvh];
}
</style>
